import {
  BeerBottle,
  BeerStein,
  Brandy,
  Champagne,
  Cheers,
  CheckCircle,
  Coffee,
  Martini,
  NotePencil,
  Wine,
} from "@phosphor-icons/react";
import { groupCatalogue } from "@/lib/menuModule";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DETAILS = [
  ["sparkling", "Sparkling", "e.g. Gledswood Estate Sparkling Cuvée", Champagne],
  ["white", "White wine", "e.g. Hunter Valley Semillon", Wine],
  ["red", "Red wine", "e.g. Mudgee Shiraz", Brandy],
  ["beer", "Beer", "Separate items with a comma", BeerBottle],
  ["non_alcoholic", "Non-alcoholic", "Separate items with a comma", Coffee],
];

// Reception package artwork: keyword match so a renamed/extra package still gets an icon.
const PACKAGE_ICONS = [
  [/non-?alcohol|mocktail|soft/i, Coffee],
  [/premium|spirit|cocktail/i, Martini],
  [/cellar|estate|vineyard|wine/i, Wine],
  [/classic|beer|standard/i, BeerStein],
];

const packageIcon = (name = "") =>
  (PACKAGE_ICONS.find(([pattern]) => pattern.test(name)) || [null, Cheers])[1];

// Beverage package picker shared by the client Drinks page and the staff planning editor.
export function DrinksSelection({ catalogue, form, setForm, locked = false }) {
  const packages = groupCatalogue(catalogue).drinks || [];
  return (
    <div className="space-y-8" data-testid="drinks-selection">
      <section>
        <h2 className="flex items-center gap-2 font-heading text-3xl text-primary">
          <Cheers size={28} weight="light" className="shrink-0 text-accent" aria-hidden="true" />
          Beverage package
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your package starts when guests enter the reception. All alcohol is supplied and served
          by our licensed team.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {packages.map((item) => {
            const active = form.package === item.name;
            const Icon = packageIcon(item.name);
            return (
              <button
                key={item.item_id}
                type="button"
                disabled={locked && !active}
                aria-pressed={active}
                onClick={() => !locked && setForm({ ...form, package: item.name })}
                data-testid={`drinks-package-${item.item_id}`}
                data-selected={active ? "true" : "false"}
                className={[
                  "flex flex-col rounded-lg border p-5 text-left transition-all duration-200",
                  active
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-card hover:-translate-y-0.5 hover:border-secondary hover:shadow-md",
                  locked && !active ? "opacity-50" : "",
                ].join(" ")}
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="flex items-center gap-3">
                    <span
                      className={[
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-200",
                        active ? "bg-primary text-primary-foreground" : "bg-secondary/40 text-primary",
                      ].join(" ")}
                      data-testid={`drinks-package-icon-${item.item_id}`}
                    >
                      <Icon size={22} weight="light" aria-hidden="true" />
                    </span>
                    <span className="font-heading text-xl text-primary">{item.name}</span>
                  </span>
                  {active && (
                    <CheckCircle size={20} weight="fill" className="shrink-0 text-primary" />
                  )}
                </span>
                <span className="mt-2 text-sm text-muted-foreground">{item.description}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card p-6">
        <p className="eyebrow">Your selections (optional)</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Tell us the specific drops you would like from your package.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {DETAILS.map(([key, label, hint, Icon]) => (
            <div key={key}>
              <Label className="eyebrow flex items-center gap-2" htmlFor={`drinks-${key}`}>
                <Icon
                  size={16}
                  weight="light"
                  className="shrink-0 text-accent"
                  aria-hidden="true"
                  data-testid={`drinks-icon-${key}`}
                />
                {label}
              </Label>
              <Input
                id={`drinks-${key}`}
                className="mt-2"
                value={form[key] || ""}
                disabled={locked}
                onChange={(event) => setForm({ ...form, [key]: event.target.value })}
                data-testid={`field-${key}`}
              />
              <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label className="eyebrow flex items-center gap-2" htmlFor="drinks-notes">
              <NotePencil
                size={16}
                weight="light"
                className="shrink-0 text-accent"
                aria-hidden="true"
              />
              Notes for the Events Team
            </Label>
            <Textarea
              id="drinks-notes"
              className="mt-2"
              rows={3}
              value={form.notes || ""}
              disabled={locked}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
              data-testid="field-notes"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
